import { useEffect, useState } from "react";
import { X, Plus, Trash } from "@phosphor-icons/react";
import { api, extractApiError } from "../lib/api";

const DOMAINS = ["Strength", "Endurance", "Mobility", "ACFT"];
const LEVELS = ["Beginner", "Intermediate", "Advanced"];
const EQUIPMENT_OPTIONS = [
  "None", "Barbell", "Dumbbell", "Kettlebell", "Cable", "Machine",
  "Sandbag", "Sled", "Pull-up Bar", "Bench", "Box", "Medball",
  "Resistance Band", "TRX", "Rower", "Bike", "Other",
];

const SECTION_TITLE = "label-mono text-volt";
const INPUT = "w-full bg-background border border-border px-3 py-2 text-sm focus:border-volt focus:outline-none";
const LABEL = "block text-xs uppercase tracking-widest text-muted-foreground mb-1";

export default function CustomExerciseModal({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [name, setName] = useState(initial?.name || "");
  const [domain, setDomain] = useState(initial?.domain || "Strength");
  const [level, setLevel] = useState(initial?.level || "Intermediate");
  const [equipment, setEquipment] = useState(initial?.equipment || "None");
  const [bodyPart, setBodyPart] = useState(initial?.body_part || "Full Body");
  const [instructions, setInstructions] = useState(
    (initial?.instructions && initial.instructions.length > 0) ? initial.instructions : [""],
  );
  const [cues, setCues] = useState(initial?.cues || []);
  const [imageUrl, setImageUrl] = useState(initial?.image_url || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const updateAt = (arr, setArr) => (i, v) => {
    const next = [...arr];
    next[i] = v;
    setArr(next);
  };
  const addRow = (arr, setArr, val = "") => () => setArr([...arr, val]);
  const removeAt = (arr, setArr) => (i) => setArr(arr.filter((_, idx) => idx !== i));

  const submit = async () => {
    setErr("");
    if (name.trim().length < 2) return setErr("Name must be at least 2 characters.");
    const cleanInstr = instructions.map((s) => s.trim()).filter(Boolean);
    if (cleanInstr.length === 0) return setErr("Add at least one instruction step so others know how to perform the drill.");
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        domain,
        level,
        equipment,
        body_part: bodyPart,
        instructions: cleanInstr,
        cues: cues.map((s) => s.trim()).filter(Boolean),
        alternates: initial?.alternates || [],
        image_url: imageUrl.trim() || null,
        notes: notes.trim() || null,
      };
      const res = editing
        ? await api.put(`/exercises/${initial.id}`, payload)
        : await api.post("/exercises", payload);
      onSaved(res.data);
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="custom-exercise-modal"
    >
      <div
        className="w-full max-w-2xl bg-card border border-border my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-border">
          <div>
            <div className="label-mono text-volt">// {editing ? "EDIT" : "NEW"} CUSTOM DRILL</div>
            <h2 className="font-display text-2xl font-black mt-1">
              {editing ? "Edit Custom Exercise" : "Add Custom Exercise"}
            </h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">
              Drills you add show up in your library, Plan Builder picker, and AI Coach prompts.
              Admin can promote good ones to the global library so every operator can use them.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-volt p-1" data-testid="close-custom-modal" aria-label="Close">
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Name */}
          <div>
            <label className={LABEL}>Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sandbag Bear Hug Squat"
              className={INPUT}
              data-testid="custom-name"
            />
          </div>

          {/* Domain / Level / Equipment / Body Part — 2 col grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Domain *</label>
              <select value={domain} onChange={(e) => setDomain(e.target.value)} className={INPUT} data-testid="custom-domain">
                {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Level</label>
              <select value={level} onChange={(e) => setLevel(e.target.value)} className={INPUT} data-testid="custom-level">
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Equipment</label>
              <select value={equipment} onChange={(e) => setEquipment(e.target.value)} className={INPUT} data-testid="custom-equipment">
                {EQUIPMENT_OPTIONS.map((eq) => <option key={eq} value={eq}>{eq}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Body Part</label>
              <input
                value={bodyPart}
                onChange={(e) => setBodyPart(e.target.value)}
                placeholder="Lower Body, Push, Core..."
                className={INPUT}
              />
            </div>
          </div>

          {/* Instructions */}
          <div>
            <div className="flex items-end justify-between mb-1">
              <label className={SECTION_TITLE}>// INSTRUCTIONS</label>
              <button
                onClick={addRow(instructions, setInstructions)}
                type="button"
                className="text-xs uppercase tracking-widest text-volt inline-flex items-center gap-1 hover:underline"
                data-testid="add-instruction"
              >
                <Plus size={12} weight="bold" /> Add Step
              </button>
            </div>
            <div className="space-y-2">
              {instructions.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <span className="font-mono text-muted-foreground text-xs mt-2 w-6">{i + 1}.</span>
                  <textarea
                    value={s}
                    onChange={(e) => updateAt(instructions, setInstructions)(i, e.target.value)}
                    rows={2}
                    placeholder={i === 0 ? "Set up position…" : "Next cue…"}
                    className={`${INPUT} resize-none`}
                    data-testid={`instruction-${i}`}
                  />
                  <button
                    onClick={() => removeAt(instructions, setInstructions)(i)}
                    disabled={instructions.length === 1}
                    className="text-muted-foreground hover:text-destructive disabled:opacity-30 mt-2"
                    aria-label="Remove step"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Cues */}
          <div>
            <div className="flex items-end justify-between mb-1">
              <label className={SECTION_TITLE}>// COACHING CUES (OPTIONAL)</label>
              <button
                onClick={addRow(cues, setCues)}
                type="button"
                className="text-xs uppercase tracking-widest text-volt inline-flex items-center gap-1 hover:underline"
                data-testid="add-cue"
              >
                <Plus size={12} weight="bold" /> Add Cue
              </button>
            </div>
            <div className="space-y-2">
              {cues.length === 0 && (
                <div className="text-xs text-muted-foreground italic">e.g. "Chest up", "Brace before unrack"</div>
              )}
              {cues.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    value={c}
                    onChange={(e) => updateAt(cues, setCues)(i, e.target.value)}
                    placeholder="Short coaching reminder…"
                    className={INPUT}
                  />
                  <button
                    onClick={() => removeAt(cues, setCues)(i)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Remove cue"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Image URL */}
          <div>
            <label className={LABEL}>Reference Image URL (optional)</label>
            <input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://… (paste a public image link)"
              className={INPUT}
            />
            {imageUrl && (
              <img
                src={imageUrl}
                alt="preview"
                className="mt-2 max-h-40 object-contain border border-border"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
          </div>

          {/* Notes */}
          <div>
            <label className={LABEL}>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything else worth flagging — substitutes, gear sizing, cautions…"
              rows={2}
              className={`${INPUT} resize-none`}
            />
          </div>

          {err && <div className="text-red-400 text-sm" data-testid="custom-error">{err}</div>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs uppercase tracking-widest border border-border text-muted-foreground hover:text-foreground"
            data-testid="cancel-custom"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-5 py-2 text-xs uppercase tracking-widest bg-volt text-black font-display font-black disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="save-custom"
          >
            {busy ? "Saving..." : editing ? "Save Changes" : "Save Drill"}
          </button>
        </div>
      </div>
    </div>
  );
}
